// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// This file provides the provides functionality to get Microsoft Graph data.

/* global console fetch */

/**
 *  Calls a Microsoft Graph API and returns the response.
 *
 * @param accessToken The access token to use for the request.
 * @param path Path component of the URI, e.g., "/me". Should start with "/".
 * @param queryParams Query parameters, e.g., "?$select=name,id". Should start with "?".
 * @returns
 */

import { showNotification } from "./utiltyfunctions";
import { dumpdebug } from "./utiltyfunctions";
import { setGlobalconst, getGlobalconst } from "./utiltyfunctions";
import { validateEmail } from "./utiltyfunctions";
import { encodeToWindows1252 } from "./utiltyfunctions";
import {
  PROPTOEMAIL,
  PROPCCEMAIL,
  PROPBCCEMAIL,
  PROPBODYTYPE,
  PROPBODYCONTENT,
  PROPSUBJECT,
  NOTIF_FAIL,
  PROPFROMEMAIL,
  GSubjectADD,
} from "./globalconstants";

export async function makeGraphRequest(accessToken: string, path: string, queryParams: string): Promise<any> {
  if (!path) throw new Error("path is required.");
  if (!path.startsWith("/")) throw new Error("path must start with '/'.");
  if (queryParams && !queryParams.startsWith("?")) throw new Error("queryParams must start with '?'.");

  const query = queryParams || "";

  const response = await fetch(`https://graph.microsoft.com/v1.0${path}${query}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json", // Good practice to include
    },
  });

  if (response.ok) {
    const data = await response.json();
    console.log(data);
    return data;
  } else {
    // Tip: Graph API returns detailed errors in the body, response.statusText might just say "Bad Request"
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error?.message || response.statusText;
    throw new Error(`Graph API Error (${response.status}): ${errorMessage}`);
  }
}

// always should be called on load
export async function getFromRecipients(): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      var fromaddressdisplay = Office.context.mailbox.userProfile.displayName;
      var fromaddressemail = Office.context.mailbox.userProfile.emailAddress;
      var outputList = "0:<" + fromaddressdisplay + "><" + fromaddressemail + ">~";
      dumpdebug("TRACE - FROMRECIEPT", "List of FROM email - " + outputList);
      setGlobalconst(PROPFROMEMAIL, outputList);
      resolve(outputList);
    } catch (error) {
      reject(new Error("Function getFromREcipients failed" + error));
    }
  });
}

export async function getToRecipients(): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.cast.item.toItemCompose(Office.context.mailbox.item).to.getAsync(function (result: { value: any }) {
      let arrayOfToRecipients = result.value;
      let outputList = "";
      let validationFailed: boolean = false;
      try {
        let key = 0;
        for (const email of arrayOfToRecipients) {
          outputList = outputList + key + ":<" + email.displayName + "><" + email.emailAddress + ">~";
          if (!validateEmail(email.emailAddress)) {
            validationFailed = true;
            showNotification(NOTIF_FAIL, "Email address '" + email.emailAddress + "' isn't formatted correctly");
            reject(new Error("TO Email address '" + email.emailAddress + "' isn't formatted correctly"));
          }
          key++;
        }
      } catch (error) {
        reject(new Error("Function getToREcipients failed" + error));
      }
      if (validationFailed) {
        reject(new Error("Function getToREcipients failed"));
      } else {
        dumpdebug("TRACE - TORECIEPT", "List of TO email - " + outputList);
        setGlobalconst(PROPTOEMAIL, outputList);
        resolve(outputList);
      }
    });
  });
}

export async function getCCRecipients(): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.cast.item.toItemCompose(Office.context.mailbox.item).cc.getAsync(function (result: { value: any }) {
      let arrayOfToRecipients = result.value;
      let outputList = "";
      let validationFailed: boolean = false;
      try {
        let key = 0;
        for (const email of arrayOfToRecipients) {
          outputList = outputList + key + ":<" + email.displayName + "><" + email.emailAddress + ">~";
          if (!validateEmail(email.emailAddress)) {
            validationFailed = true;
            showNotification(NOTIF_FAIL, "CC Email address '" + email.emailAddress + "' isn't formatted correctly");
            reject(new Error("CC Email address '" + email.emailAddress + "' isn't formatted correctly"));
          }
          key++;
        }
      } catch (error) {
        reject(new Error("Function getCCREcipients failed" + error));
      }
      if (validationFailed) {
        reject(new Error("Function getCCREcipients failed"));
      } else {
        dumpdebug("TRACE - CCRECIEPT", "List of TO email - " + outputList);
        setGlobalconst(PROPCCEMAIL, outputList);
        resolve(outputList);
      }
    });
  });
}

export async function getBCCRecipients(): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.cast.item.toItemCompose(Office.context.mailbox.item).bcc.getAsync(function (result: { value: any }) {
      let arrayOfToRecipients = result.value;
      let outputList = "";
      let validationFailed: boolean = false;
      try {
        let key = 0;
        for (const email of arrayOfToRecipients) {
          outputList = outputList + key + ":<" + email.displayName + "><" + email.emailAddress + ">~";
          if (!validateEmail(email.emailAddress)) {
            validationFailed = true;
            showNotification(NOTIF_FAIL, "BCC Email address '" + email.emailAddress + "' isn't formatted correctly");
            reject(new Error("BCC Email address '" + email.emailAddress + "' isn't formatted correctly"));
          }
          key++;
        }
      } catch (error) {
        reject(new Error("Function getBccREcipients failed" + error));
      }
      if (validationFailed) {
        reject(new Error("Function getBccREcipients failed"));
      } else {
        dumpdebug("TRACE - BCCRECIEPT", "List of TO email - " + outputList);
        setGlobalconst(PROPBCCEMAIL, outputList);
        resolve(outputList);
      }
    });
  });
}

export async function getBodyMessage(): Promise<Uint8Array> {
  // get the message type
  Office.cast.item.toItemCompose(Office.context.mailbox.item).body.getTypeAsync(function (result: { value: string }) {
    setGlobalconst(PROPBODYTYPE, result.value);
  });
  // now get the message body.
  //  The new Return promise ensure that the code is stopped and executed before returning.
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item;
    item.body.getAsync(Office.CoercionType.Text, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        const messageBody = encodeToWindows1252(asyncResult.value);
        setGlobalconst(PROPBODYCONTENT, asyncResult.value);
        resolve(messageBody);
      } else {
        reject(new Error("Failed to get message body:", asyncResult.error.message));
      }
    });
  });
}

export async function getBodyCurrentMessage(accessToken: string): Promise<Uint8Array> {
  // get the message type
  let path: string = "me/messages/${messageId}";
  const response: any = await makeGraphRequest(accessToken, path, "");

  return new Promise((resolve, reject) => {
    const test: string = "HELLO WORLD";
    const messageBody = encodeToWindows1252(response);
    if (test.length > 0) {
      resolve(messageBody);
    } else {
      reject(new Error("Failed to get message body:"));
    }
  });
}

export async function setBodyMessage(insertText: string): Promise<number> {
  // get the message type
  const item = Office.context.mailbox.item;
  item.body.getTypeAsync(function (result: { value: string }) {
    setGlobalconst(PROPBODYTYPE, result.value);
  });
  // now get the message body.
  //  The new Return promise ensure that the code is stopped and executed before returning.
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item;
    item.body.setAsync(insertText, { coercionType: Office.CoercionType.Html }, function (asyncResult) {
      // Display the result to the user
      if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
        resolve(0);
      } else {
        resolve(-1);
      }
    });
  });
}

// Helper methods for Subject of email.
export async function setSubject(): Promise<number> {
  return new Promise((resolve, reject) => {
    Office.cast.item.toItemCompose(Office.context.mailbox.item).subject.getAsync(function (result: { value: string }) {
      setGlobalconst(PROPSUBJECT, result.value);
      Office.cast.item
        .toItemCompose(Office.context.mailbox.item)
        .subject.setAsync(getGlobalconst(PROPSUBJECT) + GSubjectADD);
    });
    resolve(0);
  });
}

export async function resetSubject(): Promise<number> {
  return new Promise((resolve, reject) => {
    Office.cast.item.toItemCompose(Office.context.mailbox.item).subject.getAsync(function () {
      Office.cast.item.toItemCompose(Office.context.mailbox.item).subject.setAsync(getGlobalconst(PROPSUBJECT));
      resolve(0);
    });
  });
}
